// Tool Registry — single source of truth for all 26 tools

export type ToolStatus = 'ready' | 'coming-soon' | 'beta';
export type ToolCategory = 'PDF Editing' | 'Image Edit' | 'Scan' | 'Text Tools' | 'Audio Tools';

export interface Tool {
  id: string;
  title: string;
  /** @deprecated use title */
  name: string;
  description: string;
  category: ToolCategory;
  /** Heroicon or emoji icon name */
  icon: string;
  route: string;
  status: ToolStatus;
  keywords: string[];
  // legacy colour helpers kept for existing card components
  color: string;
  bgColor: string;
}

export const TOOL_CATEGORIES: ToolCategory[] = [
  'PDF Editing',
  'Image Edit',
  'Audio Tools',
  'Scan',
  'Text Tools',
];

export const CATEGORY_META: Record<
  ToolCategory,
  { color: string; bgColor: string; emoji: string; description: string }
> = {
  'PDF Editing': {
    color: '#DC2626',
    bgColor: '#FEF2F2',
    emoji: '📄',
    description: 'Merge, split, watermark, sign and convert PDF files in your browser.',
  },
  'Image Edit': {
    color: '#2563EB',
    bgColor: '#EFF6FF',
    emoji: '🖼️',
    description: 'Compress, resize, crop, convert and enhance images instantly.',
  },
  Scan: {
    color: '#7C3AED',
    bgColor: '#F5F3FF',
    emoji: '📷',
    description: 'Scan documents, ID cards and extract text using your camera or uploaded images.',
  },
  'Text Tools': {
    color: '#059669',
    bgColor: '#ECFDF5',
    emoji: '✏️',
    description: 'Count words, convert case, clean text and encode/decode strings.',
  },
  'Audio Tools': {
    color: '#D97706',
    bgColor: '#FFFBEB',
    emoji: '🎵',
    description: 'Convert, trim, merge, record and enhance audio files entirely in your browser.',
  },
};

export const ALL_TOOLS: Tool[] = [
  // ─── PDF Editing ────────────────────────────────────────────────────────────
  {
    id: 'merge-pdf',
    title: 'Merge PDF',
    name: 'Merge PDF',
    description: 'Combine multiple PDFs into one document',
    category: 'PDF Editing',
    icon: '📎',
    route: '/tools/merge-pdf',
    status: 'ready',
    keywords: ['merge PDF online', 'combine PDF files', 'join PDF', 'PDF merger free'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'split-pdf',
    title: 'Split PDF',
    name: 'Split PDF',
    description: 'Extract pages or split PDF into parts',
    category: 'PDF Editing',
    icon: '✂️',
    route: '/tools/split-pdf',
    status: 'ready',
    keywords: ['split PDF online', 'extract PDF pages', 'PDF splitter free', 'divide PDF'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'add-watermark',
    title: 'Add Watermark',
    name: 'Add Watermark',
    description: 'Add text watermark to your PDF',
    category: 'PDF Editing',
    icon: '💧',
    route: '/tools/add-watermark',
    status: 'ready',
    keywords: ['add watermark to PDF', 'PDF watermark online', 'stamp PDF', 'watermark PDF free'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'add-signature',
    title: 'Add Signature',
    name: 'Add Signature',
    description: 'Draw and place signature on PDF',
    category: 'PDF Editing',
    icon: '✍️',
    route: '/tools/add-signature',
    status: 'ready',
    keywords: ['sign PDF online', 'add signature to PDF', 'PDF signature free', 'e-sign PDF'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'page-reorder',
    title: 'Page Reorder',
    name: 'Page Reorder',
    description: 'Rearrange pages in your PDF',
    category: 'PDF Editing',
    icon: '🔀',
    route: '/tools/page-reorder',
    status: 'ready',
    keywords: ['reorder PDF pages', 'rearrange PDF', 'move PDF pages', 'PDF page organizer'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'pdf-to-images',
    title: 'PDF to Images',
    name: 'PDF to Images',
    description: 'Convert PDF pages to JPG/PNG images',
    category: 'PDF Editing',
    icon: '🖼️',
    route: '/tools/pdf-to-images',
    status: 'ready',
    keywords: ['PDF to image online', 'convert PDF to JPG', 'PDF to PNG free', 'export PDF pages'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'images-to-pdf',
    title: 'Images to PDF',
    name: 'Images to PDF',
    description: 'Convert images to a PDF document',
    category: 'PDF Editing',
    icon: '📋',
    route: '/tools/images-to-pdf',
    status: 'ready',
    keywords: ['convert image to PDF', 'images to PDF online', 'JPG to PDF', 'PNG to PDF free'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'extract-pdf-text',
    title: 'Extract PDF Text',
    name: 'Extract PDF Text',
    description: 'Extract all text content from PDF',
    category: 'PDF Editing',
    icon: '📝',
    route: '/tools/extract-pdf-text',
    status: 'ready',
    keywords: ['extract text from PDF', 'PDF text extractor', 'copy text from PDF', 'PDF to text online'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  {
    id: 'compress-pdf',
    title: 'Compress PDF',
    name: 'Compress PDF',
    description: 'Reduce PDF file size significantly',
    category: 'PDF Editing',
    icon: '🗜️',
    route: '/tools/compress-pdf',
    status: 'ready',
    keywords: ['compress PDF online', 'reduce PDF size', 'PDF compressor free', 'shrink PDF'],
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },

  // ─── Image Edit ─────────────────────────────────────────────────────────────
  {
    id: 'background-remover',
    title: 'Background Remover',
    name: 'Background Remover',
    description: 'Remove image background automatically',
    category: 'Image Edit',
    icon: '🪄',
    route: '/tools/background-remover',
    status: 'ready',
    keywords: ['remove image background free', 'background remover online', 'transparent background', 'remove bg'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'image-format-converter',
    title: 'Image Format Converter',
    name: 'Image Format Converter',
    description: 'Convert images between JPG, PNG, and WebP formats',
    category: 'Image Edit',
    icon: '🔄',
    route: '/tools/image-format-converter',
    status: 'ready',
    keywords: ['image format converter', 'convert jpg to png', 'convert png to webp', 'image converter online'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'image-compress',
    title: 'Image Compress',
    name: 'Image Compress',
    description: 'Compress JPG, PNG, WebP images',
    category: 'Image Edit',
    icon: '🗜️',
    route: '/tools/image-compress',
    status: 'ready',
    keywords: ['free image compressor', 'compress image online', 'reduce image size', 'image optimizer'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'image-resize',
    title: 'Image Resize',
    name: 'Image Resize',
    description: 'Resize images to any dimension',
    category: 'Image Edit',
    icon: '↔️',
    route: '/tools/image-resize',
    status: 'ready',
    keywords: ['resize image online', 'image resizer free', 'change image dimensions', 'scale image'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'image-crop',
    title: 'Image Crop',
    name: 'Image Crop',
    description: 'Crop images to custom dimensions',
    category: 'Image Edit',
    icon: '✂️',
    route: '/tools/image-crop',
    status: 'ready',
    keywords: ['crop image online', 'image cropper free', 'trim image', 'cut image online'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'format-converter',
    title: 'Format Converter',
    name: 'Format Converter',
    description: 'Convert between JPG, PNG, WebP formats',
    category: 'Image Edit',
    icon: '🔄',
    route: '/tools/format-converter',
    status: 'ready',
    keywords: ['convert image format online', 'JPG to PNG', 'PNG to WebP', 'image format converter free'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'merge-images',
    title: 'Merge Images',
    name: 'Merge Images',
    description: 'Combine multiple images side by side',
    category: 'Image Edit',
    icon: '🧩',
    route: '/tools/merge-images',
    status: 'ready',
    keywords: ['merge images online', 'combine images free', 'stitch images', 'collage maker online'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'image-to-pdf',
    title: 'Image to PDF',
    name: 'Image to PDF',
    description: 'Convert images to PDF document',
    category: 'Image Edit',
    icon: '📄',
    route: '/tools/image-to-pdf',
    status: 'ready',
    keywords: ['convert image to PDF', 'image to PDF online', 'photo to PDF free', 'JPG to PDF'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'qr-generator',
    title: 'QR Generator',
    name: 'QR Generator',
    description: 'Generate QR codes from text or URL',
    category: 'Image Edit',
    icon: '⬛',
    route: '/tools/qr-generator',
    status: 'ready',
    keywords: ['QR code generator online', 'create QR code free', 'generate QR code', 'URL to QR code'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },
  {
    id: 'qr-scanner',
    title: 'QR Scanner',
    name: 'QR Scanner',
    description: 'Scan QR codes from image or camera',
    category: 'Image Edit',
    icon: '📷',
    route: '/tools/qr-scanner',
    status: 'ready',
    keywords: ['QR code scanner online', 'scan QR code free', 'read QR code', 'QR code reader'],
    color: '#2563EB',
    bgColor: '#EFF6FF',
  },

  // ─── Scan ────────────────────────────────────────────────────────────────────
  {
    id: 'camera-scan',
    title: 'Camera Scan',
    name: 'Camera Scan',
    description: 'Scan documents using your camera',
    category: 'Scan',
    icon: '📸',
    route: '/tools/camera-scan',
    status: 'ready',
    keywords: ['scan document online', 'camera scanner free', 'document scanner browser', 'mobile document scan'],
    color: '#7C3AED',
    bgColor: '#F5F3FF',
  },
  {
    id: 'id-card-scan',
    title: 'ID Card Scan',
    name: 'ID Card Scan',
    description: 'Scan and digitize ID cards',
    category: 'Scan',
    icon: '🪪',
    route: '/tools/id-card-scan',
    status: 'beta',
    keywords: ['ID card scanner online', 'scan ID card free', 'digitize ID card', 'identity card scan'],
    color: '#7C3AED',
    bgColor: '#F5F3FF',
  },
  {
    id: 'extract-text-ocr',
    title: 'Extract Text OCR',
    name: 'Extract Text OCR',
    description: 'Extract text from images using OCR',
    category: 'Scan',
    icon: '🔍',
    route: '/tools/extract-text-ocr',
    status: 'ready',
    keywords: ['OCR online free', 'extract text from image', 'image to text converter', 'optical character recognition'],
    color: '#7C3AED',
    bgColor: '#F5F3FF',
  },

  // ─── Text Tools ──────────────────────────────────────────────────────────────
  {
    id: 'word-counter',
    title: 'Word Counter',
    name: 'Word Counter',
    description: 'Count words, characters, and sentences',
    category: 'Text Tools',
    icon: '🔢',
    route: '/tools/word-counter',
    status: 'ready',
    keywords: ['word counter online', 'character counter free', 'count words in text', 'word count tool'],
    color: '#059669',
    bgColor: '#ECFDF5',
  },
  {
    id: 'case-converter',
    title: 'Case Converter',
    name: 'Case Converter',
    description: 'Convert text case: upper, lower, title',
    category: 'Text Tools',
    icon: 'Aa',
    route: '/tools/case-converter',
    status: 'ready',
    keywords: ['text case converter online', 'uppercase lowercase converter', 'title case converter', 'change text case'],
    color: '#059669',
    bgColor: '#ECFDF5',
  },
  {
    id: 'text-cleaner',
    title: 'Text Cleaner',
    name: 'Text Cleaner',
    description: 'Remove extra spaces and clean text',
    category: 'Text Tools',
    icon: '🧹',
    route: '/tools/text-cleaner',
    status: 'ready',
    keywords: ['text cleaner online', 'remove extra spaces', 'clean text free', 'strip whitespace online'],
    color: '#059669',
    bgColor: '#ECFDF5',
  },
  {
    id: 'base64',
    title: 'Base64 Encode/Decode',
    name: 'Base64 Encode/Decode',
    description: 'Encode or decode Base64 strings',
    category: 'Text Tools',
    icon: '🔐',
    route: '/tools/base64',
    status: 'ready',
    keywords: ['Base64 encoder decoder online', 'encode Base64 free', 'decode Base64 string', 'Base64 converter'],
    color: '#059669',
    bgColor: '#ECFDF5',
  },

  // ─── Audio Tools ─────────────────────────────────────────────────────────────
  {
    id: 'audio-converter',
    title: 'Audio Converter',
    name: 'Audio Converter',
    description: 'Convert audio files between MP3, WAV, OGG, AAC, FLAC and more',
    category: 'Audio Tools',
    icon: '🔄',
    route: '/tools/audio-converter',
    status: 'ready',
    keywords: ['audio converter online free', 'convert mp3 to wav', 'convert audio format', 'audio file converter browser'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'audio-trimmer',
    title: 'Audio Trimmer',
    name: 'Audio Trimmer',
    description: 'Trim and cut audio files to any length',
    category: 'Audio Tools',
    icon: '✂️',
    route: '/tools/audio-trimmer',
    status: 'ready',
    keywords: ['audio trimmer online free', 'cut audio online', 'trim mp3 online', 'audio cutter browser'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'audio-merger',
    title: 'Audio Merger',
    name: 'Audio Merger',
    description: 'Merge multiple audio files into one',
    category: 'Audio Tools',
    icon: '🔗',
    route: '/tools/audio-merger',
    status: 'ready',
    keywords: ['merge audio files online', 'combine audio files free', 'join mp3 online', 'audio joiner browser'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'volume-booster',
    title: 'Volume Booster',
    name: 'Volume Booster',
    description: 'Boost or reduce audio volume',
    category: 'Audio Tools',
    icon: '🔊',
    route: '/tools/volume-booster',
    status: 'ready',
    keywords: ['volume booster online free', 'increase audio volume', 'boost mp3 volume', 'audio volume changer'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'audio-speed-changer',
    title: 'Audio Speed Changer',
    name: 'Audio Speed Changer',
    description: 'Change audio playback speed without pitch shift',
    category: 'Audio Tools',
    icon: '⏩',
    route: '/tools/audio-speed-changer',
    status: 'ready',
    keywords: ['audio speed changer online', 'change mp3 speed free', 'slow down audio online', 'speed up audio browser'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'pitch-changer',
    title: 'Pitch Changer',
    name: 'Pitch Changer',
    description: 'Change audio pitch without affecting speed',
    category: 'Audio Tools',
    icon: '🎼',
    route: '/tools/pitch-changer',
    status: 'ready',
    keywords: ['pitch changer online free', 'change audio pitch', 'pitch shifter browser', 'transpose audio online'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'voice-recorder',
    title: 'Voice Recorder',
    name: 'Voice Recorder',
    description: 'Record voice and audio from your microphone',
    category: 'Audio Tools',
    icon: '🎙️',
    route: '/tools/voice-recorder',
    status: 'ready',
    keywords: ['voice recorder online free', 'record audio browser', 'microphone recorder online', 'record voice online'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'extract-audio',
    title: 'Extract Audio from Video',
    name: 'Extract Audio from Video',
    description: 'Extract audio track from video files',
    category: 'Audio Tools',
    icon: '🎬',
    route: '/tools/extract-audio',
    status: 'ready',
    keywords: ['extract audio from video online', 'video to mp3 free', 'rip audio from video browser', 'convert video to audio'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'extract-audio-from-video',
    title: 'Extract Audio from Video',
    name: 'Extract Audio from Video',
    description: 'Extract MP3, WAV or M4A audio from MP4, WebM or MOV video files',
    category: 'Audio Tools',
    icon: '🎬',
    route: '/tools/extract-audio-from-video',
    status: 'ready',
    keywords: ['extract audio from video online', 'video to mp3 free', 'mp4 to mp3 online', 'rip audio from video browser', 'convert video to audio'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'ringtone-maker',
    title: 'Ringtone Maker',
    name: 'Ringtone Maker',
    description: 'Create ringtones by trimming audio to 30 seconds',
    category: 'Audio Tools',
    icon: '📱',
    route: '/tools/ringtone-maker',
    status: 'ready',
    keywords: ['ringtone maker online free', 'create ringtone from mp3', 'make ringtone browser', 'custom ringtone maker'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'fade-audio',
    title: 'Fade In / Fade Out',
    name: 'Fade In / Fade Out',
    description: 'Add fade in and fade out effects to audio',
    category: 'Audio Tools',
    icon: '🌅',
    route: '/tools/fade-audio',
    status: 'ready',
    keywords: ['audio fade in fade out online', 'add fade effect to audio', 'fade mp3 online free', 'audio fade tool browser'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'audio-fade',
    title: 'Audio Fade In / Fade Out',
    name: 'Audio Fade In / Fade Out',
    description: 'Add smooth fade in and fade out effects with preset or custom durations using ffmpeg.wasm',
    category: 'Audio Tools',
    icon: '🌅',
    route: '/tools/audio-fade',
    status: 'ready',
    keywords: ['audio fade in fade out online', 'add fade effect to audio', 'fade mp3 online free', 'audio fade tool browser', 'fade in audio online', 'fade out audio online'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'remove-silence',
    title: 'Remove Silence',
    name: 'Remove Silence',
    description: 'Automatically remove silent parts from audio',
    category: 'Audio Tools',
    icon: '🔇',
    route: '/tools/remove-silence',
    status: 'ready',
    keywords: ['remove silence from audio online', 'silence remover free', 'auto remove silence mp3', 'trim silence audio browser'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
  {
    id: 'audio-metadata-remover',
    title: 'Audio Metadata Remover',
    name: 'Audio Metadata Remover',
    description: 'Strip metadata and tags from audio files',
    category: 'Audio Tools',
    icon: '🏷️',
    route: '/tools/audio-metadata-remover',
    status: 'ready',
    keywords: ['remove audio metadata online', 'strip mp3 tags free', 'audio metadata remover browser', 'remove id3 tags online'],
    color: '#D97706',
    bgColor: '#FFFBEB',
  },
];

// ─── Helper functions ────────────────────────────────────────────────────────

export function getToolsByCategory(category: ToolCategory): Tool[] {
  return ALL_TOOLS.filter((t) => t.category === category);
}

export function getToolById(id: string): Tool | undefined {
  return ALL_TOOLS.find((t) => t.id === id);
}

export function getToolsByStatus(status: ToolStatus): Tool[] {
  return ALL_TOOLS.filter((t) => t.status === status);
}

export function searchTools(query: string): Tool[] {
  const q = query.toLowerCase();
  return ALL_TOOLS.filter(
    (t) =>
      t.title.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.keywords.some((k) => k.toLowerCase().includes(q))
  );
}
